import React from 'react';
import { ratingsAPI } from '../services/api';
import ScopeBadge from '../components/ScopeBadge';
import StatusChip from '../components/StatusChip';
import EmployeePicker from '../components/EmployeePicker';
import useScopeFilters from '../hooks/useScopeFilters';
import { useAuth } from '../context/AuthContext';
import { formatDuration } from '../utils/format';
import {
    Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Skeleton, Alert, Stack, TextField, Select, MenuItem, FormControl,
    InputLabel, Pagination, Chip, InputAdornment,
} from '@mui/material';
import { SearchRounded } from '@mui/icons-material';

const ALL = '';
const STATUSES = ['Productive', 'UnProductive', 'Neutral'];

export default function ProductivityRating() {
    const { isAdmin } = useAuth();
    // Powers the "Apply changes to" Team/Unit pickers below with only the
    // caller's own units/teams — a non-admin can't be offered (or, per the
    // backend's authorizeScopeTarget, actually apply changes to) a
    // team/unit outside their own privilege scope.
    const {
        units, teams: scopedTeams, unitId, setUnitId, teamId, setTeamId, showUnitFilter,
    } = useScopeFilters('settings.productivity_rating');
    const [scopeType, setScopeType] = React.useState(isAdmin ? 'company' : 'team'); // 'company' | 'team' | 'employee'
    const [employee, setEmployee]   = React.useState(null);
    const [search, setSearch]       = React.useState('');
    const [status, setStatus]       = React.useState(ALL);
    const [page, setPage]           = React.useState(1);
    const [records, setRecords]     = React.useState([]);
    const [pagination, setPagination] = React.useState({ total: 0, pages: 1 });
    const [loading, setLoading]     = React.useState(true);
    const [saving, setSaving]       = React.useState(null); // key of the row currently saving
    const [error, setError]         = React.useState('');
    const [notice, setNotice]       = React.useState('');

    React.useEffect(() => { setPage(1); }, [search, status]);

    const load = React.useCallback(() => {
        setLoading(true);
        setError('');
        ratingsAPI.list({ search: search || undefined, status: status || undefined, page })
            .then((res) => {
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load ratings');
                setRecords(res.data.records || []);
                setPagination(res.data.pagination || { total: 0, pages: 1 });
            })
            .catch((err) => setError(err.message || 'Failed to load ratings'))
            .finally(() => setLoading(false));
    }, [search, status, page]);

    React.useEffect(() => { load(); }, [load]);

    const scopeReady = scopeType === 'company' || (scopeType === 'team' && teamId) || (scopeType === 'employee' && employee);

    const handleRate = async (row, newStatus) => {
        if (!scopeReady) { setError('Pick a Team or Employee for the scope first.'); return; }
        const key = `${row.app_name}-${row.element}`;
        setSaving(key);
        setError('');
        setNotice('');
        try {
            const res = await ratingsAPI.save({
                app_name: row.app_name, element: row.element, status: newStatus,
                scope_type: scopeType, team_id: scopeType === 'team' ? teamId : undefined,
                user_id: scopeType === 'employee' ? employee?.user_id : undefined,
            });
            setNotice(`Updated ${res.data.modified} record${res.data.modified === 1 ? '' : 's'} for "${row.app_name}".`);
            setRecords((prev) => prev.map((r) => (r.app_name === row.app_name && r.element === row.element ? { ...r, status: newStatus } : r)));
        } catch (err) {
            setError('Failed to save rating. Please try again.');
        } finally {
            setSaving(null);
        }
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h2" sx={{ mb: 0.5 }}>Productivity Rating</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">
                            {loading ? 'Loading…' : `${pagination.total} apps/sites seen`}
                        </Typography>
                        <ScopeBadge featureKey="settings.productivity_rating" />
                    </Stack>
                </Box>
                <TextField
                    size="small" placeholder="Search app or domain…" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
                />
            </Stack>

            {/* Scope — every status change below applies within this scope */}
            <Paper sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Apply changes to:</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems={{ sm: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Scope</InputLabel>
                        <Select label="Scope" value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
                            {isAdmin && <MenuItem value="company">Whole Company</MenuItem>}
                            <MenuItem value="team">A Team</MenuItem>
                            <MenuItem value="employee">One Employee</MenuItem>
                        </Select>
                    </FormControl>

                    {scopeType === 'team' && (
                        <>
                            {showUnitFilter && (
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Unit</InputLabel>
                                    <Select label="Unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                                        <MenuItem value={ALL}>All Units</MenuItem>
                                        {units.map((u) => <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Team</InputLabel>
                                <Select label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                                    <MenuItem value={ALL}>Select a team…</MenuItem>
                                    {scopedTeams.map((t) => <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {scopeType === 'employee' && (
                        <EmployeePicker value={employee} onChange={setEmployee} sx={{ minWidth: 260 }} />
                    )}

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter Status</InputLabel>
                        <Select label="Filter Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <MenuItem value={ALL}>All Statuses</MenuItem>
                            {['Productive', 'UnProductive', 'Neutral', 'UnRated'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Stack>
                {!scopeReady && (
                    <Alert severity="info" sx={{ mt: 2 }}>Pick a team or employee above before changing any status.</Alert>
                )}
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
            {notice && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setNotice('')}>{notice}</Alert>}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>App / Domain</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Total Usage</TableCell>
                                <TableCell>Current Status</TableCell>
                                <TableCell>Set Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}>
                                            <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : records.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No apps/sites match this filter.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                records.map((r) => {
                                    const key = `${r.app_name}-${r.element}`;
                                    return (
                                        <TableRow key={key} sx={{ '&:last-child td': { border: 0 } }}>
                                            <TableCell>{r.app_name}</TableCell>
                                            <TableCell>
                                                <Chip label={r.element === 'web' ? 'Website' : 'Application'} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
                                            </TableCell>
                                            <TableCell align="right">{formatDuration(r.total_time)}</TableCell>
                                            <TableCell><StatusChip status={r.status} /></TableCell>
                                            <TableCell>
                                                <FormControl size="small" sx={{ minWidth: 140 }} disabled={!scopeReady || saving === key}>
                                                    <Select
                                                        value=""
                                                        displayEmpty
                                                        onChange={(e) => handleRate(r, e.target.value)}
                                                        renderValue={() => saving === key ? 'Saving…' : 'Change to…'}
                                                    >
                                                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
